import ReactDOM from 'react-dom';
import { CloseIcon } from '../assets/icons';
import {RefObject, useEffect, useRef} from 'react';
import LoadingIcon from './shared/LoadingIcon';

type ModalProps = {
  children: React.ReactNode;
  classNames?: string;
  bottomPane?: boolean;
  fullscreen?: boolean;
  scroll?: boolean;
  fixedBottomItem?: boolean;
  fixedBottomItemChildren?: React.ReactNode;
  controls?: boolean;
  modalTitle?: string;
  setModalVisible?: (prev: false) => void;
  removePadding?: boolean;
  fullHeightBottomPane?: boolean;
  maxHeightSize?: "small" | "med" | "large";
  isLoading?: boolean;
  scrollRef?: RefObject<HTMLDivElement>;
  themed?: boolean;
  stopPropogationOnClose?: boolean;
  autoCloseSeconds?: number;
}

const Modal = ({
  children,
  classNames,
  bottomPane,
  fullscreen,
  scroll,
  fixedBottomItem,
  setModalVisible,
  controls,
  modalTitle,
  fixedBottomItemChildren,
  removePadding,
  fullHeightBottomPane,
  maxHeightSize,
  isLoading,
  scrollRef,
  themed,
  stopPropogationOnClose,
  autoCloseSeconds
}: ModalProps) => {

  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      document.body.style.removeProperty('overflow');
    }
  },[]);

  useEffect(() => {
    if (!autoCloseSeconds || !setModalVisible) return;

    timeoutRef.current = setTimeout(() => {
      setModalVisible(false);
    }, autoCloseSeconds*1000);

  }, [autoCloseSeconds]);

  const handleClose = (e: React.MouseEvent<HTMLElement>) => {
    stopPropogationOnClose && e.stopPropagation();
    e.target === e.currentTarget && setModalVisible && !isLoading && setModalVisible(false);
  }
  
  return ReactDOM.createPortal(
    <div 
      className='modal-container' 
      data-bottom-pane={bottomPane}
      data-full-height-bottom-pane={fullHeightBottomPane}
      data-fullscreen={fullscreen} 
      data-fixed-bottom-item={fixedBottomItem}
      data-remove-padding={removePadding}
      data-themed={themed}
      onClick={handleClose}
    >
      <div
        ref={scrollRef}
        data-scroll={scroll}
        data-max-height={maxHeightSize}
        className={"modal " + (classNames || "")}
      >
        {isLoading ?
          <div className="item-row center-horizontal padding">
            <LoadingIcon small />
            <p className="body">Loading...</p>
          </div> 
          : <>
            {controls ? 
              <ModalInnerWithControls
                title={modalTitle} 
                setModalVisible={setModalVisible} 
                children={children} 
                fixedBottomItem={fixedBottomItem} 
                fixedBottomItemChildren={fixedBottomItemChildren}
                bottomPane={bottomPane}
                removePadding={removePadding}
              /> 
              : children
            }
          </>
        }
      </div>
    </div>,
    document.querySelector('#modal-root')!
  );
}

type InnerProps = {
  children: React.ReactNode, 
  title?: string, 
  setModalVisible?: (prev: false) => void,
  fixedBottomItem?: boolean;
  fixedBottomItemChildren?: React.ReactNode;
  bottomPane?: boolean;
  removePadding?: boolean;
}

const ModalInnerWithControls = ({children, setModalVisible, title, fixedBottomItem, fixedBottomItemChildren, bottomPane, removePadding}: InnerProps) => {
  
  const paddingClass = bottomPane ? 'padding-small' : 'padding';
  
  return (
    <div className={fixedBottomItem ? 'bottom-item-layout no-gap' : 'item-list'}>
      <div className={`item-list ${fixedBottomItem && children ? 'bottom-border secondary-border' : '' }`}>
        {title && setModalVisible &&
          <div className={`right-item-layout ${paddingClass} remove-padding-bottom center-horizontal`}>
            <h1 className={bottomPane ? 'heading-small header' : "heading-medium header"}>{title}</h1>
            <button className='icon xs' onClick={() => setModalVisible(false)}>
              <CloseIcon />
            </button>
          </div>
        }
        {children && 
          <div className={removePadding ? 'full-height' : 'full-height ' + paddingClass}>
            {children}
          </div>
        }
      </div>
      {fixedBottomItem && fixedBottomItemChildren}
    </div>
  )
}

export default Modal;